import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation } from '@/components'
import { FixedBottomButton } from '@/components/FixedBottomButton'
import { useGetPaymentLinkInfo, useClaimPaymentLink } from '@/domain/requests'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'

export const PaymentLinkClaimScreen: React.FC = () => {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const offsets = useOffsets()

  const [claimSuccess, setClaimSuccess] = useState<{
    amount: string
    currency: string
    transaction_id: string
  } | null>(null)

  const {
    data: linkInfo,
    isLoading: isLoadingInfo,
    isError: isErrorInfo,
  } = useGetPaymentLinkInfo(code || '', !claimSuccess)

  const {
    mutateAsync: claimLink,
    isPending: isClaiming,
  } = useClaimPaymentLink()

  const handleBack = () => {
    navigate(-1)
  }

  const handleClaim = async () => {
    if (!code) return

    try {
      const result = await claimLink({ claim_code: code })
      setClaimSuccess(result)
      toast.success(t('translation:claimSuccess', 'Payment claimed!'))
    } catch (error: any) {
      const errorDetail = error?.response?.data?.detail
      const errorCode = errorDetail?.code
      const errorDescription = errorDetail?.description

      if (errorCode === 'TRANSFER_IS_NOT_ALLOWED') {
        toast.error(
          t(
            'translation:cannotClaimOwnLink',
            'You cannot claim your own payment link',
          ),
        )
      } else if (errorCode === 'LIMIT_EXCEEDED_ERROR') {
        toast.error(
          t('translation:transferLimitExceeded', 'Transfer limit exceeded'),
        )
      } else if (
        errorCode === 'INVALID_REQUEST_DATA' &&
        errorDescription?.includes('already been claimed')
      ) {
        toast.error(
          t(
            'translation:linkAlreadyClaimed',
            'This link has already been claimed',
          ),
        )
      } else if (
        errorCode === 'INVALID_REQUEST_DATA' &&
        errorDescription?.includes('canceled')
      ) {
        toast.error(
          t('translation:linkCancelled', 'This link has been cancelled'),
        )
      } else {
        toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
      }
    }
  }

  const handleDone = () => {
    navigate('/dashboard')
  }

  // Success state
  if (claimSuccess) {
    return (
      <BaseView
        flex={1}
        bgColor="background.screen"
        style={{
          paddingTop: offsets.top,
          paddingBottom: offsets.bottom,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <StickyHeaderNavigation
          title={t('translation:claimPaymentLink', 'Claim Payment')}
          showBackButton={false}
        />

        <BaseView
          flex={1}
          center
          gap={24}
          style={{ paddingTop: '70px', paddingLeft: '16px', paddingRight: '16px' }}
        >
          <BaseView
            center
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '40px',
              background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
            }}
          >
            <BaseText fontSize={36}>✓</BaseText>
          </BaseView>

          <BaseText
            fontSize={24}
            fontFamily="bold"
            color="label.default"
            textAlign="center"
          >
            {t('translation:claimSuccess', 'Payment claimed!')}
          </BaseText>

          <BaseText
            fontSize={32}
            fontFamily="bold"
            color="label.default"
            textAlign="center"
          >
            {claimSuccess.amount} {claimSuccess.currency}
          </BaseText>
        </BaseView>

        <FixedBottomButton
          buttons={{
            label: t('form:actions.done', 'Done'),
            onPress: handleDone,
            type: 'primaryGradient',
          }}
        />
      </BaseView>
    )
  }

  // Loading state
  if (isLoadingInfo) {
    return (
      <BaseView
        flex={1}
        bgColor="background.screen"
        style={{
          paddingTop: offsets.top,
          paddingBottom: offsets.bottom,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <StickyHeaderNavigation
          title={t('translation:claimPaymentLink', 'Claim Payment')}
          showBackButton={true}
          onBackPress={handleBack}
        />

        <BaseView flex={1} center style={{ paddingTop: '70px' }}>
          <BaseText color="label.description">
            {t('translation:loading', 'Loading...')}
          </BaseText>
        </BaseView>
      </BaseView>
    )
  }

  // Error or unavailable state
  if (isErrorInfo || !linkInfo || linkInfo.status === 'unavailable') {
    return (
      <BaseView
        flex={1}
        bgColor="background.screen"
        style={{
          paddingTop: offsets.top,
          paddingBottom: offsets.bottom,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <StickyHeaderNavigation
          title={t('translation:claimPaymentLink', 'Claim Payment')}
          showBackButton={true}
          onBackPress={handleBack}
        />

        <BaseView
          flex={1}
          center
          gap={16}
          style={{ paddingTop: '70px', paddingLeft: '16px', paddingRight: '16px' }}
        >
          <BaseView
            center
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '40px',
              backgroundColor: 'rgba(255, 69, 58, 0.15)',
            }}
          >
            <BaseText fontSize={36} style={{ color: '#FF453A' }}>
              ✕
            </BaseText>
          </BaseView>

          <BaseText
            fontSize={18}
            fontFamily="medium"
            color="label.description"
            textAlign="center"
          >
            {t(
              'translation:linkNoLongerAvailable',
              'This link is no longer available',
            )}
          </BaseText>
        </BaseView>

        <FixedBottomButton
          buttons={{
            label: t('form:actions.goBack', 'Go Back'),
            onPress: handleBack,
            type: 'secondaryGray',
          }}
        />
      </BaseView>
    )
  }

  // Available state - show preview and claim button
  return (
    <BaseView
      flex={1}
      bgColor="background.screen"
      style={{
        paddingTop: offsets.top,
        paddingBottom: offsets.bottom,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }}
    >
      <StickyHeaderNavigation
        title={t('translation:claimPaymentLink', 'Claim Payment')}
        showBackButton={true}
        onBackPress={handleBack}
      />

      <BaseView
        flex={1}
        center
        gap={24}
        style={{ paddingTop: '70px', paddingLeft: '16px', paddingRight: '16px' }}
      >
        <BaseView
          center
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '40px',
            background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
          }}
        >
          <BaseText fontSize={36}>🎁</BaseText>
        </BaseView>

        <BaseText
          fontSize={18}
          fontFamily="medium"
          color="label.description"
          textAlign="center"
        >
          {t(
            'translation:paymentLinkClaimDescription',
            'Someone sent you money via a payment link',
          )}
        </BaseText>

        <BaseText
          fontSize={40}
          fontFamily="bold"
          color="label.default"
          textAlign="center"
        >
          {linkInfo.amount} {linkInfo.currency}
        </BaseText>

        {linkInfo.note && (
          <BaseView
            px={16}
            py={12}
            rounded={12}
            style={{
              backgroundColor: theme.colors.background.cardItemRoot,
              width: '100%',
            }}
          >
            <BaseText
              fontSize={14}
              fontFamily="medium"
              color="label.description"
              textAlign="center"
            >
              {linkInfo.note}
            </BaseText>
          </BaseView>
        )}
      </BaseView>

      <FixedBottomButton
        buttons={{
          label: t('translation:claimPaymentLink', 'Claim Payment'),
          onPress: handleClaim,
          type: 'primaryGradient',
          isLoading: isClaiming,
          disabled: isClaiming,
        }}
      />
    </BaseView>
  )
}
